import { z } from "zod";

import { Layout, LayoutItem, PanelType } from "@/shared/state/visualization";
import { LayoutType } from "@/shared/state/visualization/schema";

import { Requirement } from "../LayoutRecord";

import { ruleStyleSchema } from "./ruleStyleSchema";

// A panel's `config`, `title`, and `initParams` are left out. A config's shape
// follows from the panel type, and a layout saved by a newer application bundle
// can carry one this bundle has never seen, so checking it here would refuse to
// open a layout that renders. Leaving them out loses nothing, since callers read
// whether the parse succeeded and discard the value it returns.
const LayoutPanelSchema = z.object({
  id: z.string(),
  type: z.enum(PanelType),
  rules: z.array(z.string()),
});

const LayoutItemSchema = z.object({
  id: z.string(),
  type: z.literal(LayoutType.Panel),
  relativeSize: z.number(),
  isResizing: z.boolean(),
});

const LayoutSchema: z.ZodType<Layout> = z.object({
  id: z.string(),
  axis: z.enum(["x", "y"]),
  relativeSize: z.number(),
  children: z.array(z.lazy(() => z.union([LayoutSchema, LayoutItemSchema]))),
});

/**
 * Builds the validator for a stored layout definition around the schema for one
 * rule requirement. Each layout schema version supplies its own requirement
 * schema and shares the rest of the definition.
 *
 * Past the field shapes, the returned schema rejects a definition whose parts
 * disagree:
 *
 * 1. A rule that no panel lists.
 * 2. A layout item naming a panel the definition does not hold.
 */
export function createLayoutDefinitionSchema(
  requirementSchema: z.ZodType<Requirement>,
) {
  // An empty `requirements` list is accepted: the matcher answers such a rule
  // with NO_REQUIREMENTS_SPECIFIED, which the panel shows as "This panel has no
  // data requirements specified" instead of the layout failing to open.
  const LayoutRuleSchema = z.object({
    id: z.string(),
    requirements: z.array(requirementSchema),
    style: ruleStyleSchema.optional(),
  });

  return z
    .object({
      layout: LayoutSchema,
      panels: z.record(z.string(), LayoutPanelSchema),
      rules: z.array(LayoutRuleSchema),
    })
    .superRefine((def, ctx) => {
      // 1. Panels display the data bound by the rules they list, so a rule no
      // panel lists would bind data that never reaches the screen.
      const usedRules = new Set(
        Object.values(def.panels).flatMap((panel) => panel.rules),
      );

      def.rules.forEach((rule, index) => {
        if (!usedRules.has(rule.id)) {
          ctx.addIssue({
            code: "custom",
            message: `Rule ${rule.id} is not referenced by any panel`,
            path: ["rules", index],
          });
        }
      });

      // 2. A layout item's id is the id of the panel that fills its slot, so an
      // item naming no panel leaves that slot with nothing to render.
      const panelIds = new Set(Object.keys(def.panels));
      const validateLayoutItems = (
        node: Layout | LayoutItem,
        path: (string | number)[],
      ) => {
        if ("children" in node) {
          node.children.forEach((child, index) =>
            validateLayoutItems(child, [...path, "children", index]),
          );
        } else if (!panelIds.has(node.id)) {
          ctx.addIssue({
            code: "custom",
            message: `Layout item ${node.id} does not correspond to any panel`,
            path,
          });
        }
      };
      validateLayoutItems(def.layout, ["layout"]);
    });
}
